/**
 * 分類マスター詳細（`/masters/$type/$id`）— 分類マスターの全型を 1 ルートで受ける。
 *
 * 読む先は新しい表（`spec_graph_nodes`）である。見出しの下にタブを 2 つ置く（Dhik 2026-09-29
 * 「本当に出すべき情報を 1 or 2 個に」「情報をタブで分けるとかも良い」）。既定のタブは型の名前
 * （「動詞」「単位」「ステージ」）で、**このマスターが何を表すかの答えの欄だけ**を先に出す —
 * 動詞は到達状態、単位は表し方、ステージは入口と出口。残りの欄は「ほかの欄」に畳み、名前と
 * 同じ値の欄（動詞と単位の「名前」）は出さない。2 つ目のタブ「使われている場所」には、この
 * マスターを指している要素を出す。アクターとドメインは答えの欄を決めていないので、欄を全部出す。
 *
 * **値の無い欄も出す。** 欄ごと消すと、読み手には「この型にその欄が無い」のか「欄はあるが
 * 未設定」なのかが見分けられず、埋めるべき欄が画面から永久に見えなくなる。空の欄は「未設定」
 * と書く（畳んだ中でも同じ）。
 *
 * ステージは、欄と逆引きに加えて**鎖の前後のステージ**を畳んで出す。前後は読取が返したもの
 * （共通部の 1 関数が入口から導いたもの）で、画面は入口を辿り直さない。ステージの欄は
 * 名前の作り（目的語（種別）＋ 動詞（名詞形））・入口（業務 → 結末）・出口・説明の 1 行ずつに
 * 畳み、見出しにステージの図への入口を置く。種別と名詞形は用語集と動詞の一覧の読取から引く。
 *
 * 型の札・未翻訳の印は名前の title に、欄の値の未翻訳の印は値の title に回す。更新日時は出さない。
 *
 * 逆引きは**欄ごとに 1 行**にする。同じ要素が 2 つの欄で同じマスターを指すことはあり、畳むと
 * どの宣言から来た参照かが消える。
 *
 * 全型を 1 ルートで受けるため、パスパラメータの型名は取得の前に検証する。実在しない型名と
 * 読めない ID（番号でない）は未存在（`NotFoundState`）に倒す。検証せずに取得へ渡すと、BFF の
 * 入力検証エラーが通信障害と同じ見え方になり、利用者には「壊れている」としか映らない。
 *
 * 復帰導線はホームにする。この画面は全型を 1 ルートで扱うので、型ごとの一覧（`/actors` /
 * `/domains` / `/verbs` / `/units`）へ戻すと、一覧を持たないステージだけ行き先が無くなる。
 *
 * 案件モード（`?pj=`）では、型の名前のタブに欄を 欄 ／ 変更前 ／ 変更後 の 3 列に並べる（案件モードの原則）。
 * 3 列では欄を畳まず全部並べ、列見出しの補足と空札の文言は title に回す。
 * 同じマスターを変更前（`side: 'before'`）と変更後（`side: 'after'`）の 2 回引く。案件が
 * 足したマスターは変更前を、消したマスターは変更後を引かずに空札にする — 引くと必ず未存在で
 * 返り、案件の姿を見に来た利用者に「見つかりません」が出る。**案件で 1 欄も変わっていなければ
 * 3 列にせず、案件なしと同じ通常の表示で描く**（`isPjUnchanged`）。ステージの図への入口と
 * 逆引きのリンクは案件を持ったまま繋ぐ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスター 1 件を、答えの欄を先に出す型の名前のタブと、逆引きの「使われている場所」のタブに分けて描く。
 * @graph-connects useMasterDetail [calls] 分類マスター detail の取得
 * @graph-connects PjCompareTable [calls] 案件モードの 3 列の表
 * @graph-connects DetailTabs [calls] 見出しの下のタブ
 */
import type { MasterDetailOutput, MasterDetailType } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailTabs } from '../components/spec/DetailTabs.js';
import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { SpecNodeLink } from '../components/spec/SpecNodeLink.js';
import { SpecLabelBadges, displayLabel } from '../components/spec/UntranslatedBadge.js';
import {
  toElementNumber,
  useMasterDetail,
} from '../hooks/queries/spec-graph-element-master-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { isMasterDetailType } from '../lib/spec-node-links.js';
import { nodeTypeLabelKey } from '../lib/spec-node-types.js';
import { FoldedSection } from './folded-section.js';
import {
  fieldLabelKey,
  MasterFieldValue,
  UnsetValue,
  type MasterField,
} from './master-field-value.js';
import {
  StageFieldRows,
  stageFieldGroups,
  StageGroupValue,
  useStageNameMaterials,
  type StageNameMaterials,
} from './master-stage-fields.js';
import {
  buildPjCompareRows,
  isPjUnchanged,
  PjCompareTable,
  type PjSides,
} from './pj-compare-table.js';
import { usePjNodePresence } from './pj-master-presence.js';
import { pjTextOf } from './pj-search.js';
import { HOME_ROUTE, MASTER_DETAIL_ROUTE } from './route-paths.js';
import { StageLink } from './view-links/flow.js';

/** ローディング時に出すスケルトンの区画数。集約された detail の区画数に揃える。 @graph-connects none */
const SKELETON_SECTIONS = 2;

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスター詳細を、型名の検証と取得状態で出し分けて描く。
 * @graph-connects isMasterDetailType [calls] パスパラメータの型名検証
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 */
export function MasterDetailPage() {
  const { t } = useTranslation();
  const { type, id } = useParams({ from: MASTER_DETAIL_ROUTE });
  const pj = pjTextOf(useSearch({ from: MASTER_DETAIL_ROUTE }).pj);

  if (!isMasterDetailType(type)) {
    return (
      <NotFoundState id={t('masterDetail.unknownType', { type, id })} action={<BackToHomeLink />} />
    );
  }

  return <MasterDetailContent type={type} id={id} pj={pj} />;
}

/**
 * ホームへの復帰導線。未存在の 3 経路（未知の型名 / 読めない ID / 未存在の ID）で同じものを出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスター詳細の行き止まりからホームへ戻る導線を描く。
 * @graph-connects HOME_ROUTE [references] ホーム
 */
function BackToHomeLink() {
  const { t } = useTranslation();

  return (
    <Link to={HOME_ROUTE} className="text-sm text-[var(--color-accent-primary)] underline">
      {t('spec.backToHome')}
    </Link>
  );
}

/** `MasterDetailContent` の props。 @graph-connects none */
interface MasterDetailContentProps {
  /** 検証済みの型名。 */
  type: MasterDetailType;
  /** パスパラメータの ID。番号に読めない値もここへ来る。 */
  id: string;
  /** 重ねる案件番号。未指定ならマスターを読む。 */
  pj?: string;
}

/**
 * 型名が検証済みのマスター detail を、案件モードかどうかで出し分ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検証済みのマスター指定を、読めない ID は未存在へ、案件モードなら左右比較へ、それ以外は 1 面の詳細へ振り分ける。
 * @graph-connects MasterDetailSingle [calls] 1 面の詳細
 * @graph-connects MasterDetailCompare [calls] 案件モードの左右比較
 */
function MasterDetailContent({ type, id, pj }: MasterDetailContentProps) {
  /*
   * 番号に読めない ID は未存在に倒す。作り直しの ID は番号なので、古いブックマークが持つ
   * パス形の ID（`actor/stylist` 等）はここへ来る。取得を撃つと入力検証エラーになり、
   * 「無い」が「壊れている」として出る。
   */
  const nodeId = toElementNumber(id);
  if (nodeId === undefined) {
    return <NotFoundState id={id} action={<BackToHomeLink />} />;
  }
  const pjNumber = toElementNumber(pj);
  if (pjNumber === undefined) {
    return <MasterDetailSingle type={type} id={id} />;
  }
  return <MasterDetailCompare type={type} id={id} nodeId={nodeId} pj={pj} pjNumber={pjNumber} />;
}

/** 取得中のスケルトン。 @graph-connects none */
function MasterDetailSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: SKELETON_SECTIONS }, (_, index) => (
        <Skeleton key={index} variant="rectangular" height="7rem" />
      ))}
    </div>
  );
}

/**
 * 案件を選んでいないときの 1 面の詳細を、取得中・未存在・失敗・本体の 4 状態で描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business master のマスター detail を取得し、取得中・未存在・失敗・本体の 4 状態で描く。
 * @graph-connects useMasterDetail [calls] 分類マスター detail の取得
 */
function MasterDetailSingle({ type, id }: { type: MasterDetailType; id: string }) {
  const { data, isLoading, error } = useMasterDetail(type, id);

  if (isNotFoundError(error)) {
    return <NotFoundState id={id} action={<BackToHomeLink />} />;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }
  if (isLoading || data === undefined) {
    return <MasterDetailSkeleton />;
  }

  return <MasterDetailBody detail={data} pj={undefined} pjNumber={undefined} />;
}

/** `MasterDetailCompare` の props。 @graph-connects none */
interface MasterDetailCompareProps {
  type: MasterDetailType;
  id: string;
  /** 番号に読めた ID。 */
  nodeId: number;
  pj?: string;
  /** 番号に読めた案件番号。 */
  pjNumber: number;
}

/**
 * 案件モードの詳細。変更前と変更後を引き、揃ってから左右に並べる。
 *
 * 片方だけで描くと、開いた直後に「差分なし」が一瞬出てから差分つきに入れ替わり、差分の有無を
 * 読み違える。両方の答え（在る姿か、在らないことの確定）が揃うまでスケルトンを出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードで分類マスターの変更前と変更後を引き、揃ってから欄ごとの左右比較で描く。
 * @graph-connects usePjNodePresence [calls] 案件が足した・消したマスターかの判定
 * @graph-connects useMasterDetail [calls] 変更前・変更後の取得
 * @graph-connects StageCompareBody [calls] ステージの左右比較
 * @graph-connects MasterDetailCompareBody [calls] ステージ以外の左右比較
 */
function MasterDetailCompare({ type, id, nodeId, pj, pjNumber }: MasterDetailCompareProps) {
  const presence = usePjNodePresence(pjNumber, nodeId);
  const before = useMasterDetail(type, id, pj, {
    side: 'before',
    enabled: presence.master === 'present',
  });
  const after = useMasterDetail(type, id, pj, {
    side: 'after',
    enabled: presence.applied === 'present',
  });

  const beforeAbsent = presence.master === 'absent' || isNotFoundError(before.error);
  const afterAbsent = presence.applied === 'absent' || isNotFoundError(after.error);
  if (beforeAbsent && afterAbsent) {
    return <NotFoundState id={id} action={<BackToHomeLink />} />;
  }
  const failure = (beforeAbsent ? null : before.error) ?? (afterAbsent ? null : after.error);
  if (failure !== null && failure !== undefined) {
    return <ErrorState error={failure} />;
  }
  const waitingBefore = !beforeAbsent && before.data === undefined;
  const waitingAfter = !afterAbsent && after.data === undefined;
  if (waitingBefore || waitingAfter) {
    return <MasterDetailSkeleton />;
  }

  const sides = {
    before: beforeAbsent ? null : (before.data ?? null),
    after: afterAbsent ? null : (after.data ?? null),
  };
  if (type === 'stage') return <StageCompareBody sides={sides} pj={pj} pjNumber={pjNumber} />;
  return <MasterDetailCompareBody sides={sides} pj={pj} pjNumber={pjNumber} groups={null} />;
}

/**
 * 欄の値を比べるための文字列。種別・本文・参照先の番号・選択肢の並びを全部含める。
 *
 * @param field 欄 1 つ。その側に欄が無ければ undefined
 * @returns 比べるための文字列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターの欄 1 つを、変更前と変更後で比べられる文字列へ写す。
 * @graph-connects none
 */
export function masterFieldCompareKey(
  field: MasterDetailOutput['fields'][number] | undefined,
): string {
  if (field === undefined) return '';
  return [field.kind, field.text, String(field.refId ?? ''), field.tokens.join('\u0000')].join(
    '\u0001',
  );
}

/**
 * 左右の欄名を、変更後の並びを先に、変更後に無い欄を後ろに足して 1 本にする。
 *
 * @param sides 左右の姿
 * @returns 欄名の並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターの変更前と変更後の欄名を、変更後の並びを保って 1 本の並びへ突き合わせる。
 * @graph-connects none
 */
export function masterFieldNames(sides: PjSides<MasterDetailOutput>): string[] {
  const names = (sides.after?.fields ?? []).map((field) => field.field);
  for (const field of sides.before?.fields ?? []) {
    if (!names.includes(field.field)) names.push(field.field);
  }
  return names;
}

/**
 * 案件モードのステージ。変更前は master の材料、変更後は案件を重ねた材料で名前の作りを組む。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードのステージを、左右それぞれの姿の材料（目的語の種別・動詞の名詞形）で名前の作りを組んで描く。
 * @graph-connects useStageNameMaterials [calls] 名前の作りの材料
 * @graph-connects MasterDetailCompareBody [calls] 欄 ／ 変更前 ／ 変更後 の本体
 */
function StageCompareBody({
  sides,
  pj,
  pjNumber,
}: {
  sides: PjSides<MasterDetailOutput>;
  pj: string | undefined;
  pjNumber: number;
}) {
  const before = useStageNameMaterials(undefined);
  const after = useStageNameMaterials(pj);
  return (
    <MasterDetailCompareBody sides={sides} pj={pj} pjNumber={pjNumber} groups={{ before, after }} />
  );
}

/**
 * 案件モードの本体。見出しと 2 つのタブ（欄の 3 列の表 ／ 逆引き。逆引きは変更後の姿、消したなら
 * 変更前）を描く。
 *
 * ステージ（`groups` を渡したとき）は、欄を 名前の作り ／ 入口 ／ 出口 の 1 行ずつに畳んで比べる。
 * 1 欄も変わっていなければ 3 列にせず、案件を持ったまま 1 面の詳細（`MasterDetailBody`）で描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードのマスター detail を、差分があれば見出しとタブ（欄ごとの左右比較・逆引き）で描き、差分が無ければ案件を持ったまま 1 面の詳細で描く。
 * @graph-connects buildPjCompareRows [calls] 欄ごとの左右の行
 * @graph-connects isPjUnchanged [calls] 差分が 1 つも無いかの判定
 * @graph-connects MasterDetailBody [calls] 差分が無いときの 1 面の詳細
 * @graph-connects PjCompareTable [calls] 3 列の表
 * @graph-connects masterFieldCompareKey [calls] 欄の値を比べるための文字列
 * @graph-connects masterFieldNames [calls] 左右の欄名を、変更後の並びを先に、変更後に無い欄を後ろに足して 1 本にする
 * @graph-connects fieldLabelKey [calls] 契約の欄名から表示名の翻訳キーを引く
 * @graph-connects stageFieldGroups [calls] ステージの欄の畳み方
 * @graph-connects StageGroupValue [calls] ステージの行 1 つの値
 * @graph-connects MasterFieldValue [calls] 欄 1 つの値
 * @graph-connects UnsetValue [calls] 片側に無い欄
 * @graph-connects MasterDetailHeader [calls] 見出し
 * @graph-connects MasterDetailTabs [calls] 見出しの下のタブ
 * @graph-connects MasterStageChainSection [calls] ステージの鎖の前後の区画
 * @graph-connects MasterReferenceList [calls] 逆引きの並び
 */
function MasterDetailCompareBody({
  sides,
  pj,
  pjNumber,
  groups,
}: {
  sides: PjSides<MasterDetailOutput>;
  /** 重ねている案件番号。差分が無いときの 1 面の詳細でステージの材料を引く。 */
  pj: string | undefined;
  pjNumber: number;
  /** ステージの左右の名前の作りの材料。ステージ以外は null */
  groups: { before: StageNameMaterials; after: StageNameMaterials } | null;
}) {
  const { t } = useTranslation();
  const shown = sides.after ?? sides.before;
  /* 左右の片方は必ず在る（両方無ければ呼び出し側が未存在へ倒す）。 */
  if (shown === null) return null;
  const pick = (detail: MasterDetailOutput, name: string) =>
    detail.fields.find((field) => field.field === name);
  const specs =
    groups === null
      ? masterFieldNames(sides).map((name) => ({
          key: name,
          label: t(fieldLabelKey(name)),
          compareKey: (detail: MasterDetailOutput) => masterFieldCompareKey(pick(detail, name)),
          render: (detail: MasterDetailOutput) => {
            const field = pick(detail, name);
            return field === undefined ? <UnsetValue /> : <MasterFieldValue field={field} />;
          },
        }))
      : stageFieldGroups(masterFieldNames(sides)).map((group) => ({
          key: group.key,
          label: t(fieldLabelKey(group.key)),
          compareKey: (detail: MasterDetailOutput) =>
            group.fields.map((name) => masterFieldCompareKey(pick(detail, name))).join('\u0002'),
          render: (detail: MasterDetailOutput) => (
            <StageGroupValue
              group={group}
              detail={detail}
              materials={detail === sides.before ? groups.before : groups.after}
            />
          ),
        }));
  const rows = buildPjCompareRows(sides, specs);
  if (isPjUnchanged(sides, rows)) {
    return <MasterDetailBody detail={shown} pj={pj} pjNumber={pjNumber} />;
  }

  return (
    <MasterDetailTabs
      detail={shown}
      pjNumber={pjNumber}
      changed
      fields={
        <>
          <PjCompareTable firstColumn={t('pjCompare.fieldColumn')} rows={rows} />
          <MasterStageChainSection detail={shown} />
        </>
      }
    />
  );
}

/** `MasterDetailBody` の props。 @graph-connects none */
interface MasterDetailBodyProps {
  detail: MasterDetailOutput;
}

/**
 * 取得済みのマスター detail（案件なし、または案件で何も変わっていないマスター）を、見出しと
 * 2 つのタブに配る。案件を選んでいれば、ステージの名前の作りは案件の材料で組み、ステージの
 * 図への入口は案件を持ったまま繋ぐ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 取得済みのマスター detail を、見出しと、答えの欄のタブ・逆引きのタブに配って描く。
 * @graph-connects MasterDetailTabs [calls] 見出しとタブ
 * @graph-connects MasterFieldsTab [calls] 型の名前のタブの中身
 */
function MasterDetailBody({
  detail,
  pj,
  pjNumber,
}: MasterDetailBodyProps & {
  /** 重ねている案件番号。ステージの名前の作りの材料を引く。案件を選んでいなければ undefined */
  pj: string | undefined;
  /** 案件番号。ステージの図へ引き継ぐ。案件を選んでいなければ undefined */
  pjNumber: number | undefined;
}) {
  return (
    <MasterDetailTabs
      detail={detail}
      pjNumber={pjNumber}
      changed={false}
      fields={<MasterFieldsTab detail={detail} pj={pj} />}
    />
  );
}

/** マスター詳細のタブ。 @graph-connects none */
type MasterTab = 'fields' | 'usage';

/**
 * 見出しと 2 つのタブ（型の名前 ／ 使われている場所 N）。既定は型の名前のタブ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスター詳細の見出しの下に、型の名前のタブと、逆引きの件数を添えた「使われている場所」のタブを並べ、選んだタブの中身を描く。
 * @graph-connects MasterDetailHeader [calls] 見出し
 * @graph-connects DetailTabs [calls] タブの並び
 * @graph-connects nodeTypeLabelKey [calls] 型の名前
 * @graph-connects MasterReferenceList [calls] 逆引きの並び
 */
function MasterDetailTabs({
  detail,
  pjNumber,
  changed,
  fields,
}: MasterDetailBodyProps & {
  /** 案件番号。ステージの図へ引き継ぐ。案件を選んでいなければ undefined */
  pjNumber: number | undefined;
  /** 型の名前のタブの中に案件の変更があるか */
  changed: boolean;
  /** 型の名前のタブの中身 */
  fields: ReactNode;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<MasterTab>('fields');

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <MasterDetailHeader detail={detail} pjNumber={pjNumber} />
      <DetailTabs
        tabs={[
          { id: 'fields', label: t(nodeTypeLabelKey(detail.nodeType)), changed },
          { id: 'usage', label: t('masterDetail.usageTab'), count: detail.referencedBy.length },
        ]}
        value={tab}
        onChange={setTab}
      />
      <div role="tabpanel" data-tab-panel={tab} className="flex flex-col gap-4">
        {tab === 'fields' ? fields : <MasterReferenceList detail={detail} />}
      </div>
    </div>
  );
}

/**
 * 型ごとに、型の名前のタブで先に出す答えの欄と、出さない欄（見出しの名前と同じ値）。ここに無い型
 * （アクター・ドメイン）は欄を全部出す。ステージの鍵は畳んだ行の鍵（`stageFieldGroups`）である。
 * @graph-connects none
 */
const ANSWER_FIELDS: Partial<
  Record<MasterDetailOutput['nodeType'], { shown: readonly string[]; omitted: readonly string[] }>
> = {
  Verb: { shown: ['reachedState'], omitted: ['label'] },
  Unit: { shown: ['notation'], omitted: ['label'] },
  Stage: { shown: ['entry', 'exit'], omitted: [] },
};

/**
 * 型の名前のタブの中身（案件なし、または案件で何も変わっていないマスター）。答えの欄を先に出し、
 * 残りを「ほかの欄」に畳む。ステージは鎖の前後も畳んで添える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターの欄を、型ごとの答えの欄だけ先に出し、残りを開閉できる「ほかの欄」に畳んで描く。
 * @graph-connects MasterFieldRow [calls] 欄 1 つの描画
 * @graph-connects StageFieldRows [calls] ステージの欄（名前の作り・入口・出口を 1 行ずつ）
 * @graph-connects stageFieldGroups [calls] ステージの畳んだ行の数
 * @graph-connects FoldedSection [calls] 開閉できる節
 * @graph-connects MasterStageChainSection [calls] ステージの鎖の前後の区画
 */
function MasterFieldsTab({ detail, pj }: MasterDetailBodyProps & { pj: string | undefined }) {
  const { t } = useTranslation();
  const layout = ANSWER_FIELDS[detail.nodeType];
  if (layout === undefined) {
    return (
      <dl className="flex flex-col">
        {detail.fields.map((field) => (
          <MasterFieldRow key={field.field} field={field} />
        ))}
      </dl>
    );
  }
  const isShown = (key: string) => layout.shown.includes(key);

  if (detail.nodeType === 'Stage') {
    const restCount = stageFieldGroups(detail.fields.map((field) => field.field)).filter(
      (group) => !isShown(group.key),
    ).length;
    return (
      <>
        <dl className="flex flex-col">
          <StageFieldRows detail={detail} pj={pj} include={isShown} />
        </dl>
        {restCount > 0 && (
          <FoldedSection name="fields" title={t('masterDetail.moreFields')} count={restCount}>
            <dl className="flex flex-col">
              <StageFieldRows detail={detail} pj={pj} include={(key) => !isShown(key)} />
            </dl>
          </FoldedSection>
        )}
        <MasterStageChainSection detail={detail} />
      </>
    );
  }

  const shown = layout.shown.flatMap((name) =>
    detail.fields.filter((field) => field.field === name),
  );
  const rest = detail.fields.filter(
    (field) => !isShown(field.field) && !layout.omitted.includes(field.field),
  );
  return (
    <>
      <dl className="flex flex-col">
        {shown.map((field) => (
          <MasterFieldRow key={field.field} field={field} />
        ))}
      </dl>
      {rest.length > 0 && (
        <FoldedSection name="fields" title={t('masterDetail.moreFields')} count={rest.length}>
          <dl className="flex flex-col">
            {rest.map((field) => (
              <MasterFieldRow key={field.field} field={field} />
            ))}
          </dl>
        </FoldedSection>
      )}
    </>
  );
}

/**
 * 見出し（型の札 ・ 名前）。型の名前と未翻訳の印は名前の title にも添え、更新日時は出さない。
 *
 * 型の札は、どのアウトプットの詳細かを見失わないために名前の左に置く（Dhik 2026-09-29「流石に
 * ないと迷子になる」）。
 *
 * ステージには見出しに「ステージの図を開く」を置く。ステージの詳細は欄の一覧で、業務フローの
 * 並びはステージの図にしか無い。案件モードなら案件を持ったまま開く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターの型の札と名前を見出しとして描き、型と未翻訳を名前の title に添え、ステージならステージの図への入口を添える。
 * @graph-connects StageLink [calls] ステージの図へのリンク
 * @graph-connects NodeTypeBadge [calls] 名前の左の型の札
 * @graph-connects nodeTypeLabelKey [calls] 型の名前
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 */
function MasterDetailHeader({
  detail,
  pjNumber,
}: {
  detail: MasterDetailOutput;
  /** 案件番号。ステージの図へ引き継ぐ。案件を選んでいなければ undefined */
  pjNumber: number | undefined;
}) {
  const { t } = useTranslation();
  const notes = [
    t(nodeTypeLabelKey(detail.nodeType)),
    detail.label !== '' && detail.untranslated ? t('spec.untranslated') : undefined,
  ].filter((note) => note !== undefined);
  return (
    <header className="flex flex-wrap items-center gap-2" data-id={detail.id}>
      <NodeTypeBadge nodeType={detail.nodeType} />
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]" title={notes.join('\n')}>
        {displayLabel(detail.label, t, detail.untranslated)}
      </h1>
      {detail.nodeType === 'Stage' && <StageLink stageId={detail.id} pjNumber={pjNumber} />}
    </header>
  );
}

/**
 * 逆引きの並び（「使われている場所」のタブの中身）。このマスターを指している要素を、欄ごとに
 * 1 行で並べる。0 件ならタブの件数 0 で分かるので何も描かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターを参照している要素を、参照元の欄ごとに 1 行で描く。
 * @graph-connects MasterReferenceRow [calls] 逆引き 1 件の描画
 */
function MasterReferenceList({ detail }: MasterDetailBodyProps) {
  if (detail.referencedBy.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {detail.referencedBy.map((reference) => (
        <li key={`${reference.field}:${String(reference.id)}`}>
          <MasterReferenceRow reference={reference} />
        </li>
      ))}
    </ul>
  );
}

/**
 * ステージの鎖の前後の区画。前後は業務マップの並びでも読める深掘りなので、見出しだけを見せて
 * 畳む。前は 1 つか無し、次は分岐のぶん複数ありうる。ステージ以外（`stageChain` が null）は
 * 区画ごと出さない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの鎖の前のステージと次のステージを、畳んだ節の中に詳細への遷移つきで描く。
 * @graph-connects FoldedSection [calls] 開閉できる節
 * @graph-connects MasterChainRow [calls] 前後のステージ 1 件の描画
 */
function MasterStageChainSection({ detail }: MasterDetailBodyProps) {
  const { t } = useTranslation();
  const chain = detail.stageChain;
  if (chain === null) return null;

  return (
    <FoldedSection name="stageChain" title={t('masterDetail.stageChainTitle')}>
      <dl className="flex flex-col">
        <div
          className="flex flex-wrap items-baseline gap-3 border-b border-[var(--color-border)] py-2"
          data-stage-prev
        >
          <dt className="min-w-32 text-xs text-[var(--color-text-muted)]">
            {t('masterDetail.prevStage')}
          </dt>
          <dd className="flex min-w-0 flex-wrap gap-2">
            {chain.prev === null ? (
              <span className="text-sm text-[var(--color-text-muted)]">
                {t('masterDetail.noPrevStage')}
              </span>
            ) : (
              <MasterChainRow reference={chain.prev} />
            )}
          </dd>
        </div>
        <div className="flex flex-wrap items-baseline gap-3 py-2" data-stage-next>
          <dt className="min-w-32 text-xs text-[var(--color-text-muted)]">
            {t('masterDetail.nextStage')}
          </dt>
          <dd className="flex min-w-0 flex-wrap gap-2">
            {chain.next.length === 0 ? (
              <span className="text-sm text-[var(--color-text-muted)]">
                {t('masterDetail.noNextStage')}
              </span>
            ) : (
              chain.next.map((reference) => (
                <MasterChainRow key={reference.id} reference={reference} />
              ))
            )}
          </dd>
        </div>
      </dl>
    </FoldedSection>
  );
}

/**
 * 鎖の前後のステージ 1 件。名前をステージの詳細への遷移にする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 鎖の前後のステージ 1 件を、名前と未翻訳の印つきで詳細へ繋ぐ。
 * @graph-connects SpecNodeLink [calls] ノード型に応じた詳細への遷移
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 */
function MasterChainRow({ reference }: MasterReferenceRowProps) {
  const { t } = useTranslation();

  return (
    <span className="flex flex-wrap items-center gap-2" data-id={reference.id}>
      <SpecNodeLink
        nodeType={reference.nodeType}
        nodeId={String(reference.id)}
        className="text-sm font-medium text-[var(--color-accent-primary)] underline"
      >
        {displayLabel(reference.label, t, reference.untranslated)}
      </SpecNodeLink>
      <SpecLabelBadges label={reference.label} untranslated={reference.untranslated} />
    </span>
  );
}

/** `MasterFieldRow` の props。 @graph-connects none */
interface MasterFieldRowProps {
  field: MasterField;
}

/**
 * 欄 1 つ。欄名と値を 1 行で出す。**値が無ければ「未設定」と書く。**
 *
 * 参照の欄は参照先の表示名だけを出す。**番号を添えない**。人は番号で要素を判別できず、表示名が
 * 未設定のとき（材料の用語が埋まっていないとき）も「未設定」と出せば足りる。番号は `data-id` に
 * だけ残す。値の未翻訳の印は札にせず、値の title に回す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 分類マスターの欄 1 つを、欄名と値（未設定を含む）で描き、未翻訳は値の title に回す。
 * @graph-connects fieldLabelKey [calls] 欄名の翻訳キー
 * @graph-connects MasterFieldValue [calls] 欄 1 つの値
 */
function MasterFieldRow({ field }: MasterFieldRowProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-baseline gap-3 border-b border-[var(--color-border)] py-2 last:border-b-0">
      <dt className="min-w-32 text-xs text-[var(--color-text-muted)]">
        {t(fieldLabelKey(field.field))}
      </dt>
      <dd
        className="min-w-0 flex flex-wrap items-baseline gap-2 text-sm text-[var(--color-text-primary)]"
        data-id={field.refId ?? undefined}
        title={field.text !== '' && field.untranslated ? t('spec.untranslated') : undefined}
      >
        <MasterFieldValue field={field} />
      </dd>
    </div>
  );
}

/** `MasterReferenceRow` の props。 @graph-connects none */
interface MasterReferenceRowProps {
  reference: MasterDetailOutput['referencedBy'][number];
}

/**
 * 逆引き 1 行。型・名前・どの欄で指しているかを 1 ブロックで出す。
 *
 * 欄名を添えるのは、動詞のように 4 つの型から別々の欄で指される集合があるためである。
 * 型バッジだけだと「業務がこの動詞を使っている」のか「業務名の後ろ半分がこの動詞」なのかが
 * 読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターの逆引き 1 件を、型・名前・参照元の欄つきで描き詳細へ繋ぐ。
 * @graph-connects SpecNodeLink [calls] ノード型に応じた詳細への遷移
 */
function MasterReferenceRow({ reference }: MasterReferenceRowProps) {
  const { t } = useTranslation();
  const nodeId = String(reference.id);

  return (
    <div className="flex flex-wrap items-center gap-2 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2">
      <NodeTypeBadge nodeType={reference.nodeType} />
      <SpecNodeLink
        nodeType={reference.nodeType}
        nodeId={nodeId}
        className="text-sm font-medium text-[var(--color-accent-primary)] underline"
      >
        {displayLabel(reference.label, t, reference.untranslated)}
      </SpecNodeLink>
      <SpecLabelBadges label={reference.label} untranslated={reference.untranslated} />
      <span className="text-xs text-[var(--color-text-muted)]">
        {t('masterDetail.referenceVia', { field: t(fieldLabelKey(reference.field)) })}
      </span>
    </div>
  );
}
